'use client'

import { createContext, useContext, useState, useEffect, useCallback, useRef, ReactNode } from 'react'

type Theme = 'light' | 'dark'

interface ThemeContextType {
  theme: Theme
  setTheme: (theme: Theme) => void
  toggleTheme: () => void
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined)

// DOM에 테마 클래스 적용하는 함수
function applyThemeToDOM(theme: Theme) {
  if (typeof document === 'undefined') return

  const root = document.documentElement
  root.classList.remove('light', 'dark')
  root.classList.add(theme)
}

export function ThemeProvider({ children }: { children: ReactNode }) {
  const themeRef = useRef<Theme>('dark')
  const [theme, setThemeState] = useState<Theme>('dark') // 서버/클라이언트 동일한 초기값
  const [mounted, setMounted] = useState(false)

  // 클라이언트에서만 localStorage 읽기
  useEffect(() => {
    const saved = localStorage.getItem('theme')
    const initial = saved === 'light' ? 'light' : 'dark'
    themeRef.current = initial
    setThemeState(initial)
    applyThemeToDOM(initial)
    setMounted(true)
  }, [])

  const setTheme = useCallback((newTheme: Theme) => {
    themeRef.current = newTheme
    setThemeState(newTheme)
    localStorage.setItem('theme', newTheme)
    applyThemeToDOM(newTheme)
  }, [])

  const toggleTheme = useCallback(() => {
    const currentTheme = themeRef.current
    const newTheme = currentTheme === 'light' ? 'dark' : 'light'
    setTheme(newTheme)
  }, [setTheme])

  // hydration 전에는 기본 테마로 렌더링
  if (!mounted) {
    return (
      <ThemeContext.Provider value={{ theme: 'dark', setTheme, toggleTheme }}>
        {children}
      </ThemeContext.Provider>
    )
  }

  return (
    <ThemeContext.Provider value={{ theme, setTheme, toggleTheme }}>
      {children}
    </ThemeContext.Provider>
  )
}

export function useTheme() {
  const context = useContext(ThemeContext)
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider')
  }
  return context
}
